// Admin → Payments Audit
//
// Date-filtered transactions for the facility, joined with patient + most
// recent delivery + that delivery's prescriptions. Read-only audit reporting
// (NOT financial reconciliation — for that you reconcile against Stripe
// payouts directly). Admin or pharmacist role.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.jsx';
import { useTenantHref } from '../lib/tenantPath.jsx';

const STATUS_OPTIONS = ['', 'succeeded', 'failed', 'pending', 'processing',
  'requires_payment_method', 'canceled', 'refunded', 'paid', 'complete'];

export default function AdminPaymentsAudit() {
  const tHref = useTenantHref();
  const { user } = useAuth();
  const allowed = user && (user.role === 'admin' || user.role === 'pharmacist');

  // Default range: last 30 days
  const initial = useMemo(() => {
    const to = new Date(); const from = new Date(); from.setDate(to.getDate() - 30);
    return { from: toLocalDateInput(from), to: toLocalDateInput(to) };
  }, []);

  const [from, setFrom] = useState(initial.from);
  const [to,   setTo]   = useState(initial.to);
  const [status, setStatus] = useState('');
  const [processor, setProcessor] = useState('');
  const [search, setSearch] = useState('');

  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState(null);

  async function load() {
    setBusy(true); setErr(null);
    try {
      const params = {
        from: new Date(from).toISOString(),
        to:   new Date(to + 'T23:59:59').toISOString(),
      };
      if (status)    params.status    = status;
      if (processor) params.processor = processor;
      if (search)    params.q         = search;
      const r = await api.paymentsAudit(params);
      setData(r);
    } catch (e) {
      setErr(e.body?.message || e.message);
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => { if (allowed) load(); /* eslint-disable-next-line */ }, []);

  if (!allowed) {
    return <div className="card p-6 text-red-700 font-medium">Admin or pharmacist role required.</div>;
  }

  const rows    = data?.payments || [];
  const summary = data?.summary  || {};

  function exportCsv() {
    const headers = ['Date', 'Patient', 'MRN', 'Amount', 'Status', 'Processor', 'Flow',
                     'Description', 'Transaction ID', 'Delivery', 'Rx numbers', 'Drugs'];
    const lines = [headers.join(',')];
    for (const r of rows) {
      const rxNums  = (r.rx_list || []).map(x => x.rx_number || '').filter(Boolean).join('; ');
      const drugs   = (r.rx_list || []).map(x => x.drug_name || '').filter(Boolean).join('; ');
      const cells = [
        new Date(r.created_at).toISOString(),
        `${r.first_name || ''} ${r.last_name || ''}`.trim(),
        r.mrn || '',
        Number(r.amount || 0).toFixed(2),
        r.status || '',
        r.processor || '',
        r.flow || '',
        r.description || '',
        r.processor_payment_id || r.processor_checkout_id || r.id,
        r.delivery_location || '',
        rxNums,
        drugs,
      ].map(csvCell);
      lines.push(cells.join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url  = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `payments_audit_${from}_to_${to}.csv`; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Payments Audit</h1>
          <p className="text-sm text-slate-500">
            Read-only transaction report. For financial reconciliation, compare against Stripe payouts.
          </p>
        </div>
        <button className="btn-ghost" onClick={exportCsv} disabled={!rows.length}>Export CSV</button>
      </div>

      {/* Filters */}
      <div className="card p-4 grid sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
        <label className="block text-sm">
          <span className="text-xs text-slate-600">From</span>
          <input type="date" className="input" value={from} onChange={e => setFrom(e.target.value)} />
        </label>
        <label className="block text-sm">
          <span className="text-xs text-slate-600">To</span>
          <input type="date" className="input" value={to} onChange={e => setTo(e.target.value)} />
        </label>
        <label className="block text-sm">
          <span className="text-xs text-slate-600">Status</span>
          <select className="input" value={status} onChange={e => setStatus(e.target.value)}>
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s || 'All'}</option>)}
          </select>
        </label>
        <label className="block text-sm">
          <span className="text-xs text-slate-600">Processor</span>
          <select className="input" value={processor} onChange={e => setProcessor(e.target.value)}>
            <option value="">All</option><option value="stripe">Stripe</option><option value="square">Square</option>
          </select>
        </label>
        <div className="flex gap-2">
          <input className="input flex-1" placeholder="Search name / MRN / txn ID"
            value={search} onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && load()} />
          <button className="btn-primary" onClick={load} disabled={busy}>{busy ? 'Loading…' : 'Apply'}</button>
        </div>
      </div>

      {err && <div className="card p-3 text-sm text-red-700 bg-red-50 border-red-200">{err}</div>}

      {/* Summary chips */}
      <div className="flex flex-wrap gap-3 text-sm">
        <Chip label="Transactions" value={String(summary.count ?? rows.length)} />
        <Chip label="Succeeded total" value={`$${summary.succeeded_total ?? '0.00'}`} accent />
        <Chip label="Range" value={`${from} → ${to}`} />
      </div>

      {/* Table */}
      <div className="card overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left">Date</th>
              <th className="px-3 py-2 text-left">Patient</th>
              <th className="px-3 py-2 text-right">Amount</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Flow</th>
              <th className="px-3 py-2 text-left">Rx / Delivery</th>
              <th className="px-3 py-2 text-left">Transaction</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map(r => (
              <tr key={r.id} className="hover:bg-slate-50">
                <td className="px-3 py-2 whitespace-nowrap">
                  {new Date(r.created_at).toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' })}
                </td>
                <td className="px-3 py-2">
                  <Link to={tHref(`/patients/${r.patient_id}`)} className="font-medium hover:underline" style={{ color: 'var(--brand-primary)' }}>
                    {r.first_name} {r.last_name}
                  </Link>
                  <div className="text-xs text-slate-500 font-mono">{r.mrn}</div>
                </td>
                <td className="px-3 py-2 text-right font-mono">{(r.currency || 'usd').toUpperCase()} ${Number(r.amount).toFixed(2)}</td>
                <td className="px-3 py-2"><StatusBadge s={r.status} /></td>
                <td className="px-3 py-2 text-xs">{r.flow}</td>
                <td className="px-3 py-2 text-xs">
                  {(r.rx_list && r.rx_list.length > 0) ? (
                    <ul className="space-y-0.5">
                      {r.rx_list.map(x => (
                        <li key={x.id}>
                          <span className="font-mono">{x.rx_number || '—'}</span>
                          {' · '}{x.drug_name}{x.strength ? ` ${x.strength}` : ''}
                        </li>
                      ))}
                    </ul>
                  ) : <span className="text-slate-400">—</span>}
                  {r.delivery_location && (
                    <div className="text-slate-500 mt-1">{r.delivery_location}</div>
                  )}
                </td>
                <td className="px-3 py-2 font-mono text-[11px] text-slate-600">
                  {r.processor_payment_id || r.processor_checkout_id || r.id}
                  {r.email_status === 'sent' && <div className="text-emerald-700 not-italic mt-0.5">📧 emailed</div>}
                  {r.sms_status   === 'queued' && <div className="text-slate-500 mt-0.5">SMS sent</div>}
                </td>
              </tr>
            ))}
            {!rows.length && !busy && (
              <tr><td colSpan={7} className="px-3 py-10 text-center text-slate-500">No transactions in this window.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Chip({ label, value, accent = false }) {
  return (
    <div className={`px-3 py-1.5 rounded-full border text-sm ${accent
      ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
      : 'bg-white border-slate-200 text-slate-700'}`}>
      <span className="text-xs uppercase tracking-wide opacity-70 mr-1.5">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}

function StatusBadge({ s }) {
  const tone = s === 'succeeded' || s === 'paid' || s === 'complete'
                 ? 'bg-emerald-100 text-emerald-800'
             : s === 'failed' || s === 'canceled'
                 ? 'bg-red-100 text-red-800'
             : 'bg-slate-100 text-slate-700';
  return <span className={`px-2 py-0.5 rounded text-xs ${tone}`}>{s || '—'}</span>;
}

function toLocalDateInput(d) {
  const tz = d.getTimezoneOffset();
  const dd = new Date(d.getTime() - tz * 60 * 1000);
  return dd.toISOString().slice(0, 10);
}

function csvCell(v) {
  const s = String(v ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
